/**
 * Phase-2 / make-cents rule IDs for Adjustment Reason filters.
 * New invoices store these in `ruleResult`; historic rows use free-text `reason`.
 * Filter query matches both (see formAdjustmentReasonFilterQuery).
 *
 * `appliesTo`: "fee" | "expense" | "both" — scopes the Adjustment Reason dropdown
 * by Fees vs Expenses tab.
 *   - AI / make-cents rules → fee only
 *   - DUPLICATE_ENTRY → both
 *   - Classic Rule Engine rules → per engine applies-to map
 */

const humanizeRuleId = (ruleId) =>
  String(ruleId || "")
    .trim()
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const APPLIES_FEE = "fee";
const APPLIES_EXPENSE = "expense";
const APPLIES_BOTH = "both";

const aiRule = (id, historicAliases, appliesTo = APPLIES_FEE) => ({
  id,
  source: "ai",
  appliesTo,
  historicAliases,
});

const reRule = (id, appliesTo, historicAliases) => ({
  id,
  source: "re",
  appliesTo,
  historicAliases,
});

/**
 * Engine rule catalog (new data). `historicAliases` covers classic `reason` text.
 * AI / make-cents flags default to fee; DUPLICATE_ENTRY is both.
 */
export const PHASE2_ADJUSTMENT_REASON_RULES = [
  // ── AI / make-cents (fee only), except DUPLICATE_ENTRY (both) ──────────
  aiRule("EXCESSIVE_TIME", [
    "Excessive Time",
    "Excessive time for the task",
    "Excessive cumulative time",
    "prior_family",
    "prior family",
  ]),
  aiRule("VAGUE_DESCRIPTION", ["Vague Description", "Vague description", "Insufficient description"]),
  aiRule("CLERICAL_TASK", ["Clerical Task", "Clerical or non-billable work", "Clerical / non-billable"]),
  aiRule("MIXED_CLERICAL", ["Mixed Clerical", "Clerical task bundled with substantive work"]),
  aiRule("INTRA_OFFICE_CONFERENCE", [
    "Intra Office Conference",
    "Intra-office conference",
    "Intra-Office Conference",
  ]),
  aiRule("VAGUE_COMMUNICATION", ["Vague Communication", "Vague communication"]),
  aiRule("LONG_DAY", ["Long Day", "Excessive time (> 8h/day)", "same_day_tk", "same day tk"]),
  aiRule("FIRM_DAILY_CAP", ["Firm Daily Cap", "same_day_firm", "same day firm"]),
  aiRule("BLOCK_BILLING", ["Block Billing", "Block billing"]),
  aiRule("MULTI_TK_SAME_TASK", [
    "Multi Tk Same Task",
    "Multiple Timekeepers Same Task",
    "same_task",
    "same task",
  ]),
  aiRule("OVERQUALIFIED_TASK", ["Overqualified Task", "Overqualified task"]),
  aiRule("DUPLICATE_ENTRY", [
    "Duplicate Entry",
    "Duplicate entry",
    "Duplicative / redundant work",
    "duplicate",
  ], APPLIES_BOTH),
  aiRule("TRAVEL_NO_APPROVAL", [
    "Travel No Approval",
    "Travel without approval",
    "Travel time without prior approval",
  ]),
  aiRule("TRAINING_TIME", ["Training Time", "Training / CLE"]),
  aiRule("MULTI_TK_SAME_TASK_APPROVED", ["Multi Tk Same Task Approved", "same_task_approved", "same task approved"]),
  aiRule("EXCESSIVE_INCREMENT", ["Excessive Increment", "Billing increment > 0.1h"]),
  aiRule("SIMPLE_TASK_OVER_BUDGET", ["Simple Task Over Budget"]),
  aiRule("BILLING_FOR_BILLING", ["Billing For Billing", "Billing for billing"]),
  aiRule("RESEARCH_OVER_2H_APPROVED", [
    "Research Over 2H Approved",
    "Research Over 2h Approved",
    "prior_research_approved",
    "prior research approved",
  ]),
  aiRule("CONFLICT_CHECK", ["Conflict Check", "Conflict check / waiver"]),
  aiRule("RESEARCH_OVER_2H", ["Research Over 2H", "Research Over 2h", "prior_research", "prior research"]),
  aiRule("ONLINE_LEGAL_RESEARCH", [
    "Online Legal Research",
    "Billing for Online Research",
    "Online legal research charge",
  ]),
  aiRule("PROHIBITED_OVERHEAD", ["Prohibited Overhead", "Prohibited office overhead"]),
  aiRule("TOTAL_MISMATCH", ["Total Mismatch", "Total ≠ hours × rate"]),
  aiRule("MULTIPLE_ATTENDANCE", [
    "Multiple Attendance",
    "multiple_attendance",
    "attendance",
    "Three or more TKs on the same task",
  ]),

  // ── Classic Rule Engine (applies-to from engine catalog) ───────────────
  reRule("WRONG_EXPENSE_CODE", APPLIES_EXPENSE, ["Wrong Expense Code", "Wrong expense code"]),
  reRule("INACTIVE_TIMEKEEPER", APPLIES_FEE, ["Inactive Timekeeper", "Inactive timekeeper"]),
  reRule("LONG_BILLING_DAY", APPLIES_FEE, ["Long Billing Day", "Long billing day", "Timekeeper billed over 8 hours"]),
  reRule("TK_RATE_EXCEEDED", APPLIES_FEE, ["TK Rate Exceeded", "Tk Rate Exceeded", "Rate Exceeded"]),
  reRule("AUTO_APPROVAL", APPLIES_BOTH, ["Auto-Approval", "Auto Approval", "Auto_Approve", "Auto Approve"]),
  reRule("BILLING_IN_EXCESS_OF_AGREED_RATE", APPLIES_BOTH, [
    "Billing in Excess of Agreed Rate",
    "Billing In Excess Of Agreed Rate",
    "Line Expense Exceeds Amount",
  ]),
  reRule("LATE_BILLING", APPLIES_BOTH, ["Late Billing", "Late Billing (60/90 day)", "Sterling - Late Billing"]),
  reRule("DISCOVERY_MOTION", APPLIES_BOTH, ["Discovery Motion", "Discovery motion"]),
  reRule("MULTI_TK_SAME_ACTIVITY", APPLIES_FEE, ["Multi-TK Same Activity", "Multi TK Same Activity", "Multi-Tk Same Activity"]),
].map((rule) => ({
  ...rule,
  // Common name with leading capital — e.g. "Excessive time"
  label: (() => {
    const common = humanizeRuleId(rule.id).toLowerCase();
    return common ? common.charAt(0).toUpperCase() + common.slice(1) : rule.id;
  })(),
  appliesTo: rule.appliesTo || APPLIES_FEE,
  historicAliases: Array.from(
    new Set([humanizeRuleId(rule.id), ...(rule.historicAliases || [])].filter(Boolean))
  ),
}));

const RULE_BY_ID = PHASE2_ADJUSTMENT_REASON_RULES.reduce((acc, rule) => {
  acc[rule.id.toUpperCase()] = rule;
  acc[rule.id.toLowerCase()] = rule;
  return acc;
}, {});

/** Lookup historic alias → rule (case-insensitive). */
const RULE_BY_ALIAS = PHASE2_ADJUSTMENT_REASON_RULES.reduce((acc, rule) => {
  (rule.historicAliases || []).forEach((alias) => {
    acc[String(alias).trim().toLowerCase()] = rule;
  });
  return acc;
}, {});

const ruleAppliesToLineItemType = (rule, lineItemType) => {
  const type = String(lineItemType || "fee").toLowerCase() === "expense"
    ? "expense"
    : "fee";
  const applies = String(rule?.appliesTo || APPLIES_FEE).toLowerCase();
  if (applies === APPLIES_BOTH) return true;
  return applies === type;
};

/**
 * Adjustment Reason dropdown options for Fees or Expenses.
 * @param {"fee"|"expense"} [lineItemType="fee"]
 */
export const getPhase2AdjustmentReasonOptions = (lineItemType = "fee") => {
  const rules = PHASE2_ADJUSTMENT_REASON_RULES.filter((rule) =>
    ruleAppliesToLineItemType(rule, lineItemType)
  );
  return rules.map((r) => ({
    id: r.id,
    value: r.id,
    label: r.label,
    appliesTo: r.appliesTo,
    source: r.source,
  }));
};

/**
 * Expand a selected filter value into all strings that should match
 * `reason` (historic) or appear inside `ruleResult` (new).
 * Accepts rule IDs, display labels ("excessive time"), or historic aliases.
 */
export const expandAdjustmentReasonMatchTerms = (selected) => {
  const raw = String(selected ?? "").trim();
  if (!raw) return [];

  // Legacy "common/id" display: "excessive time/excessive_time" → prefer id after slash.
  const slashIdx = raw.lastIndexOf("/");
  const idCandidate = slashIdx >= 0 ? raw.slice(slashIdx + 1).trim() : raw;

  const normalized = idCandidate.toUpperCase().replace(/\s+/g, "_");
  const terms = new Set([raw, idCandidate]);
  const byId = RULE_BY_ID[normalized] || RULE_BY_ID[idCandidate] || RULE_BY_ID[raw];
  const byAlias =
    RULE_BY_ALIAS[raw.toLowerCase()]
    || RULE_BY_ALIAS[idCandidate.toLowerCase()];
  const rule = byId || byAlias;
  if (rule) {
    terms.add(rule.id);
    terms.add(rule.id.toLowerCase());
    terms.add(rule.label);
    (rule.historicAliases || []).forEach((alias) => terms.add(alias));
  }
  return Array.from(terms).filter(Boolean);
};
